import React, {useState, useMemo} from 'react';
import makeComp from '../util/profiler';
import {Link, useHistory} from 'react-router-dom';
import {assertUnreachable} from '../util/types';
import * as Urls from '../util/urls';
import {
  Artifact,
  useArtifactCollectionQuery,
  useArtifactTypeQuery,
  useArtifactTypesQuery,
  useDeleteArtifactCollectionMutation,
  useMoveArtifactCollectionMutation,
  useUpdateArtifactCollectionMutation,
} from '../generated/graphql';
import styled from 'styled-components';
import {
  Button,
  Dropdown,
  DropdownItemProps,
  Icon,
  Input,
  Modal,
} from 'semantic-ui-react';
import LegacyWBIcon from './elements/LegacyWBIcon';
import _ from 'lodash';
import * as S from './ArtifactTypeSidebar.styles';
import WBPopupMenuTrigger from './elements/WBPopupMenuTrigger';
import {WBMenuOption} from './elements/WBMenu';

interface RenameArtifactModalProps {
  name: string;
  modalOpen: boolean;
  onRenameArtifact(newName: string): void;
  onClose(): void;
}

const RenameArtifactModal = makeComp(
  (props: RenameArtifactModalProps) => {
    const {name, modalOpen, onRenameArtifact, onClose} = props;
    const [newName, setNewName] = useState(name);

    return (
      <Modal size="tiny" open={modalOpen} onClose={onClose}>
        <Modal.Header>
          Are you sure you want to rename this artifact?
        </Modal.Header>
        <Modal.Content>
          <p>
            This will break all aliases associated with the current name. You
            will need to update your scripts to refer to the updated name.
          </p>
          <Input
            fluid
            value={newName}
            placeholder={'Artifact name...'}
            onChange={(e, data) => {
              setNewName(data.value);
            }}
          />
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            color="green"
            disabled={newName === name || newName === ''}
            onClick={() => {
              onRenameArtifact(newName);
            }}>
            Rename
          </Button>
        </Modal.Actions>
      </Modal>
    );
  },
  {id: 'RenameArtifactModal'}
);

interface MoveArtifactModalProps {
  entityName: string;
  projectName: string;
  modalOpen: boolean;
  onMoveArtifact(destinationArtifactTypeName: string): void;
  onClose(): void;
}

const MoveArtifactModal = makeComp(
  (props: MoveArtifactModalProps) => {
    const {entityName, projectName, modalOpen, onMoveArtifact, onClose} = props;
    const [value, setValue] = useState<string | undefined>(undefined);
    const [options, setOptions] = useState<DropdownItemProps[]>([]);
    const {loading} = useArtifactTypesQuery({
      fetchPolicy: 'no-cache',
      onCompleted: data => {
        setOptions(
          data!.project!.artifactTypes.edges.map(edge => {
            return {
              key: edge.node!.name,
              value: edge.node!.name,
              text: edge.node!.name,
            };
          })
        );
      },
      variables: {
        entityName,
        projectName,
      },
    });

    return (
      <Modal size="tiny" open={modalOpen} onClose={onClose}>
        <Modal.Header>
          Are you sure you want to move this artifact?
        </Modal.Header>
        <Modal.Content>
          <p>
            You will need to update your scripts to log new versions using the
            updated type name.
          </p>
          <Dropdown
            fluid
            search
            selection
            allowAdditions
            placeholder={'Destination artifact type...'}
            options={options}
            loading={loading}
            value={value}
            onChange={(e, data) => {
              setValue(data.value as string);
            }}
            onAddItem={(e, data) => {
              setOptions([
                {
                  key: data.value as string,
                  value: data.value as string,
                  text: data.value as string,
                },
                ...options,
              ]);
            }}
          />
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            color="green"
            disabled={value === undefined || value === ''}
            onClick={async () => {
              if (value !== undefined) {
                await onMoveArtifact(value!);
              }
            }}>
            Move
          </Button>
        </Modal.Actions>
      </Modal>
    );
  },
  {id: 'MoveArtifactModal'}
);

interface DeleteArtifactModalProps {
  modalOpen: boolean;
  onDeleteArtifact(): void;
  onClose(): void;
}

const DeleteArtifactModal = makeComp(
  (props: DeleteArtifactModalProps) => {
    return (
      <Modal size="tiny" open={props.modalOpen} onClose={props.onClose}>
        <Modal.Header>
          Are you sure you want to delete this artifact?
        </Modal.Header>
        <Modal.Content>
          <p>
            All versions of the artifact and the underlying files will be
            scheduled for deletion.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={props.onClose}>Cancel</Button>
          <Button
            color="red"
            onClick={() => {
              props.onDeleteArtifact();
              props.onClose();
            }}>
            Delete this artifact
          </Button>
        </Modal.Actions>
      </Modal>
    );
  },
  {id: 'DeleteArtifactModal'}
);

interface ArtifactHeaderPopupDropdownProps {
  style?: React.CSSProperties;
  onRenameArtifact(): void;
  onMoveArtifact(): void;
  onDeleteArtifact(): void;
}

const ArtifactHeaderPopupDropdown = makeComp(
  (props: ArtifactHeaderPopupDropdownProps) => {
    const {onRenameArtifact, onMoveArtifact, onDeleteArtifact} = props;
    const options = useMemo(() => {
      const opts: WBMenuOption[] = [
        {
          value: 'edit',
          name: 'Rename',
          icon: 'edit',
        },
        {
          value: 'move',
          name: 'Move',
          icon: 'move',
        },
        {
          value: 'delete',
          name: 'Delete',
          icon: 'delete',
        },
      ];
      return opts;
    }, []);
    type optionType = 'edit' | 'move' | 'delete';
    const optionRouter = (opt: optionType) => {
      switch (opt) {
        case 'edit':
          return onRenameArtifact();
        case 'move':
          return onMoveArtifact();
        case 'delete':
          return onDeleteArtifact();
      }
      return assertUnreachable(opt);
    };

    return (
      <WBPopupMenuTrigger
        options={options}
        onSelect={value => {
          optionRouter(value as optionType);
        }}>
        {({anchorRef, setOpen}) => (
          <S.EllipsesMenuButton
            className="row-actions-button"
            name="overflow"
            title="menu"
            ref={anchorRef}
            onClick={() => setOpen(true)}
            style={props.style}
          />
        )}
      </WBPopupMenuTrigger>
    );
  },
  {id: 'ArtifactHeaderPopupDropdown'}
);

export const ArtifactSidebarPaginate = makeComp<{
  size?: 'mini' | 'tiny';
  total?: number;
  page?: number;
  perPage?: number;
  pageInfo: {hasNextPage?: boolean; hasPreviousPage?: boolean};
  onPreviousClick?: () => void;
  onNextClick?: () => void;
}>(
  ({
    onPreviousClick,
    onNextClick,
    total = 0,
    pageInfo,
    perPage = 0,
    page = 0,
    size = 'mini',
  }) => {
    const {hasNextPage, hasPreviousPage} = pageInfo;
    if (!hasNextPage && !hasPreviousPage) {
      return null;
    }

    const pageStart = page * perPage;
    const pageEnd = page * perPage + perPage;

    return (
      <div
        style={{alignItems: 'normal', marginTop: 5}}
        className="inline-pagination">
        <div
          className="pagination-count"
          style={
            size === 'mini' ? {lineHeight: '0.5em', fontSize: '0.8em'} : {}
          }>
          {pageStart + 1} - {pageEnd} of {total}
        </div>
        <Button.Group className="pagination-buttons">
          <Button
            className="wb-icon-button only-icon page-down-button"
            size={size}
            disabled={!hasPreviousPage}
            onClick={onPreviousClick}>
            <LegacyWBIcon name="previous" />
          </Button>
          <Button
            className="wb-icon-button page-up-button only-icon"
            size={size}
            disabled={!hasNextPage}
            onClick={onNextClick}>
            <LegacyWBIcon name="next" />
          </Button>
        </Button.Group>
      </div>
    );
  },
  {id: 'ArtifactSidebarPaginate'}
);

interface ArtifactMin {
  id: string;
  name: string;
}

const ArtifactCompareButton = makeComp<{
  show?: boolean;
  onClick?: React.MouseEventHandler;
}>(
  ({show, onClick}) => {
    return show ? (
      <Button
        className="artifact-compare-button"
        size="tiny"
        style={{marginLeft: 8, padding: '2px 4px'}}
        onClick={onClick}>
        Compare
      </Button>
    ) : null;
  },
  {id: 'ArtifactCompareButton'}
);

const ArtifactItemHeader = makeComp<
  ArtifactMin & {
    entityName: string;
    projectName: string;
    artifactTypeName: string;
    artifactCommitHash?: string;
    active: boolean;
    onClick: React.MouseEventHandler<HTMLDivElement>;
  }
>(
  props => {
    const {
      id,
      name,
      active,
      entityName,
      projectName,
      artifactTypeName,
      onClick,
    } = props;

    const to = Urls.artifactSequence({
      entityName,
      projectName,
      artifactTypeName,
      artifactSequenceName: name,
    });
    const icon = active ? 'caret down' : 'caret right';
    const history = useHistory();
    const [isRenaming, setRenaming] = useState(false);
    const [isMoving, setMoving] = useState(false);
    const [isDeleting, setDeleting] = useState(false);
    const artifactTypeQuery = useArtifactTypeQuery({
      variables: {
        entityName,
        projectName,
        artifactTypeName,
      },
    });
    const refetch = async () => {
      if (!artifactTypeQuery.loading) {
        await artifactTypeQuery.refetch();
      }
    };
    const [updateArtifactCollection] = useUpdateArtifactCollectionMutation({
      onCompleted: refetch,
    });
    const [moveArtifactCollection] = useMoveArtifactCollectionMutation({
      onCompleted: refetch,
    });
    const [deleteArtifactCollection] = useDeleteArtifactCollectionMutation({
      onCompleted: refetch,
    });

    return (
      <div className={'overview-option'}>
        <RenameArtifactModal
          name={name}
          modalOpen={isRenaming}
          onRenameArtifact={async newName => {
            await updateArtifactCollection({
              variables: {
                artifactSequenceID: id,
                name: newName,
              },
            });
            setRenaming(false);
            history.replace(
              Urls.artifactSequence({
                entityName,
                projectName,
                artifactTypeName,
                artifactSequenceName: newName,
              })
            );
          }}
          onClose={async () => {
            setRenaming(false);
          }}
        />
        <MoveArtifactModal
          entityName={entityName}
          projectName={projectName}
          modalOpen={isMoving}
          onMoveArtifact={async destinationArtifactTypeName => {
            await moveArtifactCollection({
              variables: {
                artifactSequenceID: id,
                destinationArtifactTypeName,
              },
            });
            setMoving(false);
            history.push(
              Urls.artifactSequence({
                entityName,
                projectName,
                artifactTypeName: destinationArtifactTypeName,
                artifactSequenceName: name,
              })
            );
          }}
          onClose={() => setMoving(false)}
        />{' '}
        <DeleteArtifactModal
          modalOpen={isDeleting}
          onDeleteArtifact={() =>
            deleteArtifactCollection({
              variables: {
                artifactSequenceID: id ?? '',
              },
            })
          }
          onClose={() => setDeleting(false)}
        />
        <div
          className={'overview-option-inline'}
          style={{cursor: 'pointer'}}
          onClick={onClick}>
          <Icon name={icon} />
          <Link to={to}>{name}</Link>
        </div>
        <div className={'overview-option-ellipses'}>
          <ArtifactHeaderPopupDropdown
            onRenameArtifact={() => setRenaming(true)}
            onMoveArtifact={() => setMoving(true)}
            onDeleteArtifact={() => setDeleting(true)}
            style={{paddingRight: '1px'}}
          />
        </div>
      </div>
    );
  },
  {id: 'ArtifactItemHeader'}
);

const ArtifactItemString = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const ArtifactItemVersion = makeComp<
  Pick<Artifact, 'commitHash' | 'digest' | 'state'> & {
    to: string;
    isSelected: boolean;
    aliases: Array<{alias: string}>;
    isCompared: boolean;
    showCompare: boolean;
    onCompareClick: React.MouseEventHandler;
  }
>(
  props => {
    const {
      to,
      digest,
      state,
      aliases,
      isSelected,
      isCompared,
      onCompareClick,
      showCompare,
    } = props;

    const tags = aliases.map(a => a.alias);
    const version = tags.find(a => a.startsWith('v')) ?? digest;
    const aliasesStrings = tags.filter(t => t !== version);

    return (
      <Link to={to}>
        <ArtifactItemString
          className={`artifact-sidebar-item version-option ${
            isSelected ? 'selected-option' : ''
          } ${state === 'DELETED' ? 'deleted-option' : ''} ${
            isCompared ? 'compared-option' : ''
          }`}>
          {version} {aliasesStrings.join(', ')}
          <ArtifactCompareButton show={showCompare} onClick={onCompareClick} />
        </ArtifactItemString>
      </Link>
    );
  },
  {id: 'ArtifactItemVersion'}
);

const ArtifactItem = makeComp<
  ArtifactMin & {
    active: boolean;
    entityName: string;
    projectName: string;
    artifactTypeName: string;
    artifactCollectionName: string;
    artifactCommitHash?: string;
    artifactTab?: string;
    filePath?: string;
    onHeaderClick: React.MouseEventHandler;
  }
>(
  props => {
    const {
      name,
      active,
      entityName,
      projectName,
      artifactCommitHash,
      artifactCollectionName,
      artifactTypeName,
      artifactTab,
      filePath,
      onHeaderClick,
    } = props;

    const artifactsPerPage = 20;
    const history = useHistory();
    const {data, loading, refetch} = useArtifactCollectionQuery({
      variables: {
        artifactFirst: artifactsPerPage,
        entityName,
        projectName,
        artifactTypeName,
        artifactCollectionName: name,
      },
      skip: !active,
    });

    // TODO: can probably do this with react router. And is this really what
    // we want?
    const urlHash = window.location.hash.slice(1) ?? '';
    const [compareID] = urlHash.split('$');

    const artifactCollection = data?.project?.artifactType?.artifactSequence;
    const totalCount = artifactCollection?.artifacts.totalCount;
    const pageInfo = artifactCollection?.artifacts.pageInfo;
    const artifacts = artifactCollection?.artifacts.edges.map(n => n.node);
    const hasArtifacts = artifacts != null && artifacts?.length > 0;

    const fetchWithCursor = (cursor?: string) => {
      refetch({
        entityName,
        projectName,
        artifactTypeName,
        artifactCollectionName: name,
        artifactCursor: cursor,
      });
    };

    const [cursors, setCursors] = useState<string[]>([]);
    const nextPage = () => {
      const cursor = pageInfo?.endCursor;
      if (cursor != null) {
        setCursors([...cursors, cursor]);
        fetchWithCursor(cursor);
      }
    };

    const previousPage = () => {
      const tmpCursors = [...cursors];
      tmpCursors.pop();
      const cursor = _.last(tmpCursors);
      setCursors(tmpCursors);
      fetchWithCursor(cursor);
    };

    return (
      <>
        <ArtifactItemHeader {...props} onClick={onHeaderClick} />
        {active && (
          <div style={{marginLeft: 15}}>
            {loading && data == null ? (
              <span>Loading artifact...</span>
            ) : (
              <>
                {!hasArtifacts && <span>No artifacts found.</span>}
                {artifacts?.map(a => {
                  const url = Urls.artifactSubpage({
                    entityName,
                    projectName,
                    artifactTypeName,
                    artifactSequenceName: name,
                    artifactCommitHash: a.commitHash!,
                    tabName: artifactTab,
                    pathString: filePath,
                  });

                  const onCompareClick: React.MouseEventHandler = e => {
                    e.preventDefault();
                    if (artifactCommitHash == null) {
                      return;
                    }
                    history.replace(
                      Urls.artifactSubpageCompare({
                        entityName,
                        projectName,
                        artifactTypeName,
                        artifactSequenceName: artifactCollectionName,
                        artifactCommitHash,
                        tabName: artifactTab,
                        pathString: filePath,
                        compareArtifactID: a.commitHash!,
                        compareArtifactSequence: props.name,
                      })
                    );
                  };
                  const isSelected = a.commitHash === artifactCommitHash;
                  const isCompared = a.commitHash === compareID;
                  const showCompare =
                    artifactTab === 'files' &&
                    a.commitHash !== artifactCommitHash;

                  return (
                    <ArtifactItemVersion
                      key={a.id}
                      {...a}
                      isSelected={isSelected}
                      isCompared={isCompared}
                      showCompare={showCompare}
                      to={url}
                      onCompareClick={onCompareClick}
                    />
                  );
                })}
              </>
            )}
            <ArtifactSidebarPaginate
              page={cursors.length}
              perPage={artifactsPerPage}
              total={totalCount}
              pageInfo={pageInfo ?? {}}
              onPreviousClick={previousPage}
              onNextClick={nextPage}
            />
          </div>
        )}
      </>
    );
  },
  {id: 'ArtifactItem'}
);

interface Props {
  entityName: string;
  projectName: string;
  artifactTypeName: string;
  artifactCollectionName: string;
  artifactCommitHash?: string;
  artifactTab?: string;
  filePath?: string;
  artifactSequences: ArtifactMin[];
}

const ArtifactTypeSidebar = makeComp<Props>(
  props => {
    const [firstRun, setFirstRun] = useState(true);
    const [expanded, setExpanded] = useState<{[id: string]: boolean}>({});
    const {artifactSequences, artifactCollectionName} = props;
    return (
      <>
        {artifactSequences.map(a => {
          const isActive = expanded[a.id];
          const toggle = () => setExpanded(s => ({...s, [a.id]: !s[a.id]}));

          const urlHash = window.location.hash.slice(1) ?? '';
          const [, compareSequenceName] = urlHash.split('$');

          // Automatically expand sections based on the url
          const isArtifactCollection = artifactCollectionName === a.name;
          const isCompareCollection = compareSequenceName === a.name;
          if (
            (isArtifactCollection || isCompareCollection) &&
            !isActive &&
            firstRun
          ) {
            toggle();
            setFirstRun(false);
          }

          return (
            <ArtifactItem
              key={a.id}
              {...props}
              {...a}
              onHeaderClick={toggle}
              active={expanded[a.id]}
            />
          );
        })}
      </>
    );
  },
  {id: 'ArtifactTypeSidebar'}
);

export default ArtifactTypeSidebar;
